@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.engineservice.support

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardNode
import org.example.project.engineletter.RenderSupportLetterSys
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.engineservice.ServiceApi
import org.example.project.screens_sys.UnternehmenstammApi
import org.example.project.service.CustomerApi
import org.example.project.service.GlobalManager
import org.example.project.service.LizenzService
import org.example.project.service.LoginService
import org.example.project.service.RollenService
import org.example.project.service.ZustandManager
import org.example.project.settings.LogoSettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import kotlin.coroutines.resume

private const val CUS_SUPPORT_PDF_PREVIEW_ROOT_ID = "cus-support-pdf-preview-root"
private const val CUS_SUPPORT_PDF_RENDER_ATTEMPTS = 12
private const val LUMATICA_SYS_FIRMEN_ID = "ls1000"

private fun currentSupportScreenKey(): String {
    val hash = window.location.hash
    val queryScreen = hash
        .substringAfter('?', "")
        .split("&")
        .firstOrNull { it.substringBefore("=").trim() == "screen" }
        ?.substringAfter("=")
        ?.let { js("decodeURIComponent")(it) as String }
        .orEmpty()
    return queryScreen.ifBlank {
        hash.removePrefix("#/").substringBefore("?").ifBlank { "SupportFlowScreen" }
    }
}

private fun supportAccessControlledScreenKey(key: String): String =
    when (key) {
        "KontaktKalender" -> "Kalender"
        else -> key
    }

private fun supportAllowedScreens(node: String, nodeScreens: List<String>): List<String> =
    when (node) {
        DashboardNode.CUS, DashboardNode.CUS_MOBILE -> {
            val effectiveScreens = LizenzService.getEffectiveScreens().toSet()
            nodeScreens.filter { key ->
                supportAccessControlledScreenKey(key) in effectiveScreens &&
                    RollenService.hasReadPermission(supportAccessControlledScreenKey(key))
            }
        }
        DashboardNode.NET -> nodeScreens
        else -> emptyList()
    }

private fun supportNodeError(node: String): String? =
    when (node) {
        DashboardNode.CUS, DashboardNode.CUS_MOBILE, DashboardNode.NET -> null
        DashboardNode.SYS -> "SupportFlowScreen darf im SYS-Kontext nicht als Kunden-Support gestartet werden. SYS ist der Bearbeiterbereich für Supportfälle."
        else -> "SupportFlowScreen wurde mit unbekanntem Node '$node' gestartet. Erlaubt sind CUS, CUS_MOBILE und NET."
    }

private fun supportTicketValidationError(ticket: All): String? =
    when {
        ticket.id.isBlank() -> "Support-Ticket fehlerhaft: Ticket-ID fehlt. Das Backend hat kein gültiges Support-Ticket geliefert."
        ticket.firmenId.isBlank() -> "Support-Ticket fehlerhaft: Firmen-ID fehlt. Das Backend konnte den NET/CUS-Firmenkontext nicht auflösen."
        ticket.ticketnummer.isBlank() -> "Support-Ticket fehlerhaft: Ticketnummer fehlt. Das Markdown wird nicht erzeugt, weil der Supportfall unvollständig ist."
        else -> null
    }

@Composable
fun CUSSupportFlow(
    node: String = DashboardNode.CUS,
    nodeScreens: List<String> = LizenzService.getCustomerDashboardScreens(),
    isAdmin: Boolean = false
) {
    val supportLiveVersion = GlobalManager.jsonDomainVersion("support")
    val scope = rememberCoroutineScope()
    val nodeError = supportNodeError(node)

    if (nodeError != null) {
        CUSSupportLayout(
            showBackButton = false,
            hint = "Support-Konfiguration fehlerhaft: $nodeError",
            onBack = {}
        ) {
            Div({
                style {
                    width(100.percent)
                    maxWidth(980.px)
                    padding(24.px)
                    property("box-sizing", "border-box")
                }
            }) {
                Text("Support-Konfiguration fehlerhaft: $nodeError")
            }
        }
        return
    }

    var state by remember { mutableStateOf("HUB") }
    var modul by remember { mutableStateOf("") }
    var problem by remember { mutableStateOf("") }
    var ticketId by remember { mutableStateOf("") }
    var ticketNr by remember { mutableStateOf("") }
    var kiProvider by remember { mutableStateOf("") }
    var docMd by remember { mutableStateOf("") }
    var abschlussArt by remember { mutableStateOf("OFFEN") }
    var loesungsweg by remember { mutableStateOf("") }
    var hint by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var verlauf by remember { mutableStateOf<List<All>>(emptyList()) }
    var verlaufFilter by remember { mutableStateOf("ALLE") }
    var currentUser by remember { mutableStateOf(All()) }
    var kundenstamm by remember { mutableStateOf<List<All>>(emptyList()) }
    var unternehmen by remember { mutableStateOf(All()) }
    var pdfPreviewTicket by remember { mutableStateOf<All?>(null) }
    var pdfPreviewKunde by remember { mutableStateOf<All?>(null) }
    var pdfPreviewUnternehmen by remember { mutableStateOf(All()) }
    var finishedTicket by remember { mutableStateOf<All?>(null) }
    var finishedPdfUrl by remember { mutableStateOf("") }
    var pendingPdfTicket by remember { mutableStateOf<All?>(null) }
    var pendingFinishState by remember { mutableStateOf<String?>(null) }

    val currentScreen = remember { currentSupportScreenKey() }
    val allowedScreens = remember(node, nodeScreens, GlobalManager.sessionConfigVersion()) { supportAllowedScreens(node, nodeScreens) }
    val screenOptions = remember(currentScreen, allowedScreens) {
        (listOf(currentScreen) + allowedScreens).filter { it.isNotBlank() }.distinct()
    }

    fun download(file: String, content: String, mime: String) {
        val blob = Blob(arrayOf(content), BlobPropertyBag(mime))
        val url = URL.createObjectURL(blob)
        val a = document.createElement("a") as HTMLAnchorElement
        a.href = url
        a.download = file
        document.body?.appendChild(a)
        a.click()
        document.body?.removeChild(a)
        URL.revokeObjectURL(url)
    }

    suspend fun loadVerlauf() {
        verlauf = ServiceApi.loadSupport("company")
            .sortedByDescending { it.id.toIntOrNull() ?: 0 }
    }

    suspend fun ensurePreviewContextLoaded(firmenId: String) {
        if (unternehmen.firma.isBlank()) {
            unternehmen = runCatching { UnternehmenstammApi.loadUnternehmen(LUMATICA_SYS_FIRMEN_ID) ?: All() }.getOrDefault(All())
        }
        if (firmenId.isBlank()) return
        val hasKunde = kundenstamm.any { it.firmenId == firmenId || it.id == firmenId }
        if (!hasKunde) {
            val kunde = runCatching { CustomerApi.loadDetails(firmenId) }.getOrNull()
            if (kunde != null) {
                kundenstamm = (kundenstamm.filterNot { it.firmenId == firmenId || it.id == firmenId } + kunde)
            }
        }
    }

    suspend fun resumeOpenTicketIfNeeded() {
        if (isAdmin || ticketId.isNotBlank()) return
        val openTicket = ServiceApi.resumeSupport() ?: return

        ticketId = openTicket.id
        ticketNr = openTicket.ticketnummer
        modul = openTicket.screenKey
        problem = openTicket.problembeschreibung
        loesungsweg = openTicket.loesungsweg
        kiProvider = openTicket.kiAuswahl
        abschlussArt = if (openTicket.zustand == ZustandManager.Support.OFFEN) "OFFEN" else "GELOEST"
        hint = if (openTicket.zustand == ZustandManager.Support.TICKET_ERSTELLT) {
            "Willkommen im lumatica KI Support."
        } else {
            "Bitte wähle aus ob die KI den Fall gelöst hat oder nicht."
        }
        state = if (openTicket.zustand == ZustandManager.Support.TICKET_ERSTELLT) "KI" else "ABSCHLUSS"
    }

    suspend fun createTicketAndPrepareDocs() {
        val promptMd = ServiceApi.loadSupportPromptMarkdown()
        val systemMd = ServiceApi.loadSupportSystemMarkdown()
        val ticket = ServiceApi.createSupport(modul, problem) ?: run {
            hint = "Ticket konnte nicht erstellt werden."
            return
        }
        val ticketValidationError = supportTicketValidationError(ticket)
        if (ticketValidationError != null) {
            hint = ticketValidationError
            return
        }
        ticketId = ticket.id
        ticketNr = ticket.ticketnummer
        docMd = ServiceApi.buildKiSupportMarkdown(ticket, promptMd, systemMd, "Noch nicht gewählt")
    }

    suspend fun markTicketInBearbeitung(provider: String) {
        val updated = ServiceApi.markSupportKi(ticketId, provider) ?: return
        val ticketValidationError = supportTicketValidationError(updated)
        if (ticketValidationError != null) {
            hint = ticketValidationError
            return
        }
        val promptMd = ServiceApi.loadSupportPromptMarkdown()
        val systemMd = ServiceApi.loadSupportSystemMarkdown()
        docMd = ServiceApi.buildKiSupportMarkdown(updated, promptMd, systemMd, provider)
        download("${updated.ticketnummer}.md", docMd, "text/markdown;charset=utf-8")
    }

    suspend fun prepareCurrentSupportPdf(ticket: All): Boolean {
        val exportTicketId = ticket.id
        if (exportTicketId.isBlank()) {
            supportPdfFailure("Support-PDF Fehler: Ticket-ID fehlt.")
            return false
        }
        val previewFirmenId = ticket.firmenId.ifBlank { currentUser.firmenId.ifBlank { GlobalManager.currentFirmenId } }
        val loadedUnternehmen = runCatching { UnternehmenstammApi.loadUnternehmen(LUMATICA_SYS_FIRMEN_ID) ?: All() }.getOrDefault(All())
        val loadedKunde = previewFirmenId.takeIf { it.isNotBlank() }?.let {
            runCatching { CustomerApi.loadDetails(it) }.getOrNull()
        }
        unternehmen = loadedUnternehmen
        if (loadedKunde != null) {
            kundenstamm = (kundenstamm.filterNot { it.firmenId == loadedKunde.firmenId || it.id == loadedKunde.id } + loadedKunde)
        }
        pdfPreviewUnternehmen = loadedUnternehmen
        pdfPreviewKunde = loadedKunde
        pdfPreviewTicket = ticket
        return true
    }

    suspend fun exportPreparedSupportPdf(ticket: All): String {
        val exportTicketId = ticket.id
        if (exportTicketId.isBlank()) return supportPdfFailure("Support-PDF Fehler: Ticket-ID fehlt.")
        val root = waitForSupportPdfRoot(CUS_SUPPORT_PDF_PREVIEW_ROOT_ID)
            ?: return supportPdfFailure("Support-PDF Fehler: Das gerenderte Support-Dokument wurde nicht gefunden.")
        return runCatching {
            val fileName = ServiceApi.supportPdfFileName(ticket)
            val pdfFile = renderLetterDocumentPdfFile(root, fileName)
            val pdfFileSize = (pdfFile.asDynamic().size as? Number)?.toDouble() ?: 0.0
            if (pdfFileSize <= 0.0) {
                return supportPdfFailure("Support-PDF Fehler: Das gerenderte PDF ist leer.")
            }
            val uploadedUrl = ServiceApi.uploadServiceDocument("support", exportTicketId, pdfFile, fileName)
            if (uploadedUrl.isBlank()) {
                return supportPdfFailure("Support-PDF Fehler: Upload in den Service-Verbrauch ist fehlgeschlagen.")
            }
            uploadedUrl
        }.onFailure { error ->
            supportPdfFailure("Support-PDF Fehler: PDF-Rendering ist fehlgeschlagen.", error)
        }.getOrDefault("")
    }

    suspend fun openSupportTicketPdf(ticket: All) {
        var available = finishedTicket?.id == ticket.id && finishedPdfUrl.isNotBlank()
        val fileName = ServiceApi.supportPdfFileName(ticket)
        if (available && ServiceApi.openSupportDocument(ticket.id, fileName, logMissing = false)) {
            return
        }
        val manuallyClosed = ticket.manuelleLoesung.isNotBlank() ||
            ticket.manuelleFehlerursache.isNotBlank()
        if (manuallyClosed) {
            if (ServiceApi.openSupportDocument(ticket.id, fileName, logMissing = false)) return
            hint = "Support-PDF wurde beim Supportabschluss nicht gespeichert."
            return
        }
        if (ServiceApi.openSupportDocument(ticket.id, fileName, logMissing = false)) return
        if (ticket.problemGeloest && prepareCurrentSupportPdf(ticket)) {
            available = exportPreparedSupportPdf(ticket).isNotBlank()
            if (finishedTicket?.id == ticket.id) {
                finishedPdfUrl = if (available) "available" else ""
            }
        }
        if (available) {
            ServiceApi.openSupportDocument(ticket.id, fileName)
        } else {
            supportPdfFailure("Support-PDF Fehler: Es wurde kein Dokument-Link gefunden.")
        }
    }

    suspend fun finishTicket(): Boolean {
        if (ticketId.isBlank()) {
            hint = "Ticket-ID fehlt."
            return false
        }

        val solved = abschlussArt == "GELOEST"
        val provider = kiProvider.ifBlank { "Unbekannt" }

        return try {
            val updatedTicket = ServiceApi.finishSupport(
                ticketId = ticketId,
                solved = solved,
                loesungsweg = loesungsweg,
                begruendung = loesungsweg.trim(),
                provider = provider
            ) ?: run {
                supportPdfFailure("Support-PDF Fehler: Ticket wurde nicht abgeschlossen, weil die Finish-API keine Ticketdaten geliefert hat.")
                return false
            }

            finishedTicket = updatedTicket
            if (!solved) {
                finishedPdfUrl = ""
                pendingPdfTicket = null
                pendingFinishState = null
                state = "FERTIG_OFFEN"
                return true
            }

            if (!prepareCurrentSupportPdf(updatedTicket)) {
                return false
            }
            pendingPdfTicket = updatedTicket
            pendingFinishState = "FERTIG_GELOEST"
            true
        } catch (error: Throwable) {
            hint = "Ticket konnte nicht gespeichert werden."
            supportPdfFailure("Support-PDF Fehler: Ticketabschluss ist technisch fehlgeschlagen.", error)
            false
        }
    }

    LaunchedEffect(state, supportLiveVersion) {
        if (state == "VERLAUF") loadVerlauf()
    }

    LaunchedEffect(isAdmin) {
        resumeOpenTicketIfNeeded()
    }

    LaunchedEffect(pendingPdfTicket?.id) {
        val ticket = pendingPdfTicket ?: return@LaunchedEffect
        finishedPdfUrl = exportPreparedSupportPdf(ticket)
        if (finishedPdfUrl.isBlank()) {
            hint = "Ticket wurde gespeichert, aber das Support-PDF konnte nicht erzeugt werden."
            supportPdfFailure("Support-PDF Fehler: Ticket wurde gespeichert, aber es wurde kein PDF-Link erzeugt.")
            pendingPdfTicket = null
            pendingFinishState = null
            loading = false
            return@LaunchedEffect
        }
        pendingPdfTicket = null
        pendingFinishState?.let { state = it }
        pendingFinishState = null
        loading = false
    }

    LaunchedEffect(Unit) {
        currentUser = LoginService.getUser() ?: All()
        val firmenId = currentUser.firmenId.ifBlank { GlobalManager.currentFirmenId }
        ensurePreviewContextLoaded(firmenId)
    }

    LaunchedEffect(currentUser.firmenId, GlobalManager.currentFirmenId) {
        val firmenId = currentUser.firmenId.ifBlank { GlobalManager.currentFirmenId }
        ensurePreviewContextLoaded(firmenId)
    }

    CUSSupportLayout(
        showBackButton = state != "HUB",
        hint = hint,
        onBack = { if (state != "HUB") state = "HUB" }
    ) {
        when (state) {
            "HUB" -> {
                CUSSupportHub(
                    onCreateTicket = { state = "FORM" },
                    onShowHistory = { state = "VERLAUF" }
                )
            }

            "FORM" -> {
                CUSSupportTicketForm(
                    screenOptions = screenOptions,
                    modul = modul,
                    problem = problem,
                    loading = loading,
                    onModulChange = { modul = it },
                    onProblemChange = { problem = it },
                    onSubmit = {
                        if (modul.isBlank() || problem.isBlank()) {
                            hint = "Bitte Fehlerort auswählen und Problembeschreibung ausfüllen."
                            return@CUSSupportTicketForm
                        }
                        loading = true
                        scope.launch {
                            try {
                                createTicketAndPrepareDocs()
                                hint = null
                                state = "KI"
                            } finally {
                                loading = false
                            }
                        }
                    }
                )
            }

            "KI" -> {
                CUSSupportKiChoice(
                    onChatGpt = {
                        kiProvider = "ChatGPT"
                        scope.launch { markTicketInBearbeitung(kiProvider) }
                        window.open("https://chatgpt.com", "_blank")
                        state = "ABSCHLUSS"
                    },
                    onGemini = {
                        kiProvider = "Gemini"
                        scope.launch { markTicketInBearbeitung(kiProvider) }
                        window.open("https://gemini.google.com", "_blank")
                        state = "ABSCHLUSS"
                    },
                    onCopilot = {
                        kiProvider = "Copilot"
                        scope.launch { markTicketInBearbeitung(kiProvider) }
                        window.open("https://copilot.microsoft.com", "_blank")
                        state = "ABSCHLUSS"
                    }
                )
            }

            "ABSCHLUSS" -> {
                CUSSupportAbschluss(
                    abschlussArt = abschlussArt,
                    problem = problem,
                    loesungsweg = loesungsweg,
                    loading = loading,
                    onAbschlussArtChange = { abschlussArt = it },
                    onLoesungswegChange = { loesungsweg = it },
                    onSubmit = {
                        if (abschlussArt == "GELOEST" && loesungsweg.isBlank()) {
                            hint = "Bitte die Abschlussanalyse der KI einfügen."
                            return@CUSSupportAbschluss
                        }
                        if (abschlussArt != "GELOEST" && loesungsweg.isBlank()) {
                            hint = "Bitte die Abschlussanalyse der KI einfügen."
                            return@CUSSupportAbschluss
                        }
                        loading = true
                        kotlinx.coroutines.MainScope().launch {
                            try {
                                finishTicket()
                            } finally {
                                if (pendingPdfTicket == null) loading = false
                            }
                        }
                    }
                )
            }

            "VERLAUF" -> {
                CUSSupportVerlaufLayout(
                    verlauf = verlauf,
                    verlaufFilter = verlaufFilter,
                    onFilterChange = { verlaufFilter = it },
                    onOpenClosedTicket = { ticket ->
                        scope.launch { openSupportTicketPdf(ticket) }
                    }
                )
            }

            "FERTIG_GELOEST" -> CUSSupportDoneSolved(
                onOpenPdf = finishedTicket?.let { ticket ->
                    { scope.launch { openSupportTicketPdf(ticket) } }
                }
            )
            "FERTIG_OFFEN" -> CUSSupportDoneOpen(
                onOpenPdf = null
            )
        }
    }

    val fallbackPreviewFirmenId = currentUser.firmenId.ifBlank { GlobalManager.currentFirmenId }
    val fallbackPreviewFirma = currentUser.firma.ifBlank {
        kundenstamm.firstOrNull { it.firmenId == fallbackPreviewFirmenId }?.firma.orEmpty()
    }
    val previewTicket = pdfPreviewTicket ?: All(
        id = ticketId.ifBlank { "0" },
        ticketnummer = ticketNr,
        firmenId = fallbackPreviewFirmenId,
        firma = fallbackPreviewFirma,
        vorname = currentUser.vorname,
        nachname = currentUser.nachname,
        personalnummer = currentUser.personalnummer,
        standort = currentUser.standort,
        supportart = "Dashboard-KI",
        ticketgrund = "",
        problembeschreibung = problem,
        loesungsweg = loesungsweg,
        screenKey = modul,
        kiAuswahl = kiProvider
    )
    val previewUnternehmen = pdfPreviewUnternehmen.takeIf { it.firma.isNotBlank() } ?: unternehmen
    val previewKunde = pdfPreviewKunde ?: kundenstamm.firstOrNull {
        (previewTicket.firmenId.isNotBlank() && it.firmenId == previewTicket.firmenId) ||
            (previewTicket.firma.isNotBlank() && it.firma == previewTicket.firma)
    }

    if (ticketId.isNotBlank() || pdfPreviewTicket != null) {
        Div({
            style {
                position(Position.Fixed)
                left((-10000).px)
                top(0.px)
                width(794.px)
                property("pointer-events", "none")
                opacity(0)
                property("z-index", "-1")
            }
        }) {
            RenderSupportLetterSys(
                rootElementId = CUS_SUPPORT_PDF_PREVIEW_ROOT_ID,
                ticket = previewTicket,
                kunde = previewKunde,
                unternehmen = previewUnternehmen,
                logoSrc = LogoSettings.supportLetterSysLogo,
                manuelleLoesung = "",
                signaturBildSrc = "",
                signaturNameText = "",
                includeLoesungBlock = false,
                closingNameText = "${previewTicket.kiAuswahl.ifBlank { "KI" }}\nlumatica KI Support Team"
            )
        }
    }
}

private fun supportPdfFailure(message: String, error: Throwable? = null): String {
    if (error != null) {
        console.error(message, error)
    } else {
        console.error(message)
    }
    AppDialogs.alert(message)
    return ""
}

private suspend fun waitForSupportPdfRoot(rootId: String): HTMLElement? {
    repeat(CUS_SUPPORT_PDF_RENDER_ATTEMPTS) {
        awaitSupportAnimationFrame()
        val root = document.getElementById(rootId) as? HTMLElement
        if (root?.querySelector("[data-letter-pdf-page='true']") != null) return root
    }
    return null
}

private suspend fun awaitSupportAnimationFrame() {
    kotlinx.coroutines.suspendCancellableCoroutine<Unit> { continuation ->
        window.requestAnimationFrame {
            continuation.resume(Unit)
        }
    }
}
